import { DialogTitle } from '@aibees/shared/components';
import { SHARED_ROUTES as R } from '@aibees/shared/constants/routes';
import { useAddCardMutationApi } from '@aibees/shared/features/billing-profile/api/use-add-card-mutation-api';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { UpdatePaymentMethodDialogProps as Props } from './types';

export const UpdatePaymentMethodDialog = memo(
  ({ className, open, onClose }: Props) => {
    const { mutateAsync, isLoading } = useAddCardMutationApi();

    const handleAddNewPaymentMethod = () => {
      mutateAsync({
        redirect: {
          success: `${window.origin + R.billing}#${'successAddCard'}`,
          failed: `${window.origin + R.billing}#${'failedAddCard'}`,
        },
      });
    };

    return (
      <Styled.Wrapper
        className={classnames('UpdatePaymentMethodDialog', className)}
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        sx={SX.root}
      >
        <DialogTitle
          title="Payment Failed"
          icon="alert"
          iconColor="primary.500"
          onClose={onClose}
        />

        <DialogContent sx={SX.content}>
          {open && (
            <Box
              component="img"
              src="/assets/images/payment-failed-illustration.png"
              alt="Payment failed illustration"
              loading="lazy"
              sx={SX.illustration}
            />
          )}

          <Card variant="outlined">
            <CardContent padding="normal" sx={SX.message}>
              <Typography variant="body3" fontWeight={700} color="grey.700">
                The default card on file could not be charged.
              </Typography>
              <Typography variant="body3" color="grey.700">
                To resolve this, set a different saved card as your default or
                add a new payment method and mark it as default — the pending
                payment will retry automatically.
              </Typography>
            </CardContent>
          </Card>
        </DialogContent>

        <DialogActions sx={SX.footer}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleAddNewPaymentMethod}
            disabled={isLoading}
          >
            Add New Payment Method
          </Button>
        </DialogActions>
      </Styled.Wrapper>
    );
  }
);

UpdatePaymentMethodDialog.displayName = 'UpdatePaymentMethodDialog';
